import { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  useColorModeValue,
  Grid,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  StatGroup,
} from '@chakra-ui/react';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaPlus,
  FaEllipsisV,
  FaBoxOpen,
  FaDollarSign,
  FaEyeSlash,
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { Post } from '../types';
import { getPostsBySeller } from '../data/mockData';
import ItemCard from '../components/ItemCard';
import MessageInterface from '../components/MessageInterface';

export default function MyPostsPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPost, setCurrentPost] = useState<Post | null>(null);
  const toast = useToast();
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  const { isOpen: isMessageOpen, onOpen: onMessageOpen, onClose: onMessageClose } = useDisclosure();

  useEffect(() => {
    const loadUserPosts = async () => {
      setLoading(true);
      
      if (user?.userId) {
        try {
          // Get posts for current user
          const userPosts = getPostsBySeller(user.userId);
          setPosts(userPosts);
        } catch (err) {
          toast({
            title: 'Error',
            description: 'Failed to load your posts',
            status: 'error',
            duration: 3000,
          });
        }
      }
      
      setLoading(false);
    };

    if (user) {
      loadUserPosts();
    }
  }, [user, toast]);

  const handlePostAction = (post: Post) => {
    setCurrentPost(post);
    // Handle the action
  };

  const handleViewPost = (postId: string) => {
    window.location.href = `/post/${postId}`;
  };

  const handleMessage = (postId: string) => {
    setCurrentPost(posts.find(p => p.id === postId) || null);
    onMessageOpen();
  };

  const handleSendMessage = () => {
    toast({
      title: 'Message sent!',
      description: 'Your message has been sent.',
      status: 'success',
      duration: 3000,
    });
    onMessageClose();
  };

  if (loading) {
    return (
      <Container maxW="7xl" py={8}>
        <VStack spacing={4} align="center">
          <Spinner size="xl" color="brand.green.dark" />
          <Text fontSize="lg" color="gray.600">Loading your posts...</Text>
        </VStack>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxW="7xl" py={8}>
        <VStack spacing={4} align="center">
          <Text fontSize="lg" color="gray.600">Please log in to view your posts.</Text>
          <Button colorScheme="green" onClick={() => window.location.href = '/login'}>
            Login
          </Button>
        </VStack>
      </Container>
    );
  }

  const activePosts = posts.filter(p => p.status === 'active');
  const soldPosts = posts.filter(p => p.status === 'sold');
  const withdrawnPosts = posts.filter(p => p.status === 'withdrawn');

  const totalValue = activePosts.reduce((sum, post) => sum + post.price, 0);

  return (
    <Container maxW="7xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="lg" color="brand.green.dark">
              My Posts
            </Heading>
            <Text color="gray.600">
              Manage your listed items and track their performance
            </Text>
          </VStack>
          <Button
            colorScheme="green"
            leftIcon={<FaPlus />}
            size="lg"
            onClick={() => window.location.href = '/create-post'}
          >
            Create New Post
          </Button>
        </HStack>

        {/* Stats Overview */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody>
            <StatGroup>
              <Stat>
                <StatLabel>Active Posts</StatLabel>
                <StatNumber>{activePosts.length}</StatNumber>
                <StatHelpText>Currently listed</StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Total Value</StatLabel>
                <StatNumber>£{totalValue.toLocaleString()}</StatNumber>
                <StatHelpText>Active listings</StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Sold Items</StatLabel>
                <StatNumber>{soldPosts.length}</StatNumber>
                <StatHelpText>Successfully sold</StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Withdrawn</StatLabel>
                <StatNumber>{withdrawnPosts.length}</StatNumber>
                <StatHelpText>Temporarily hidden</StatHelpText>
              </Stat>
            </StatGroup>
          </CardBody>
        </Card>

        {/* Posts Tabs */}
        <Tabs variant="enclosed" colorScheme="green">
          <TabList>
            <Tab>
              <HStack>
                <FaBoxOpen />
                <Text>Active ({activePosts.length})</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <FaDollarSign />
                <Text>Sold ({soldPosts.length})</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <FaEyeSlash />
                <Text>Withdrawn ({withdrawnPosts.length})</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Active Posts Tab */}
            <TabPanel px={0}>
              {activePosts.length === 0 ? (
                <VStack spacing={4} py={8}>
                  <Text fontSize="lg" color="gray.500">
                    You don't have any active posts yet
                  </Text>
                  <Button
                    colorScheme="green"
                    leftIcon={<FaPlus />}
                    onClick={() => window.location.href = '/create-post'}
                  >
                    Create Your First Post
                  </Button>
                </VStack>
              ) : (
                                 <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={6}>
                   {activePosts.map((post) => (
                     <Box key={post.id} position="relative">
                       <ItemCard
                         post={post}
                         onViewDetails={() => handleViewPost(post.id)}
                         onMessage={() => handleMessage(post.id)}
                       />
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FaEllipsisV />}
                          variant="ghost"
                          size="sm"
                          position="absolute"
                          top={2}
                          right={2}
                          bg="white"
                          _hover={{ bg: 'gray.100' }}
                        />
                        <MenuList>
                                                     <MenuItem
                             icon={<FaEdit />}
                             onClick={() => handlePostAction(post)}
                           >
                             Edit Post
                           </MenuItem>
                           <MenuItem
                             icon={<FaEye />}
                             onClick={() => handleViewPost(post.id)}
                           >
                             View Details
                           </MenuItem>
                           <MenuItem
                             icon={<FaEyeSlash />}
                             onClick={() => handlePostAction(post)}
                           >
                             Withdraw Post
                           </MenuItem>
                           <MenuItem
                             icon={<FaTrash />}
                             onClick={() => handlePostAction(post)}
                             color="red.500"
                           >
                             Delete Post
                           </MenuItem>
                        </MenuList>
                      </Menu>
                    </Box>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* Sold Posts Tab */}
            <TabPanel px={0}>
              {soldPosts.length === 0 ? (
                <VStack spacing={4} py={8}>
                  <Text fontSize="lg" color="gray.500">
                    No sold items yet
                  </Text>
                </VStack>
              ) : (
                                 <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={6}>
                   {soldPosts.map((post) => (
                     <Box key={post.id} position="relative">
                       <ItemCard
                         post={post}
                         onViewDetails={() => handleViewPost(post.id)}
                         onMessage={() => handleMessage(post.id)}
                       />
                      <Badge
                        position="absolute"
                        top={2}
                        left={2}
                        colorScheme="green"
                        variant="solid"
                      >
                        SOLD
                      </Badge>
                    </Box>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* Withdrawn Posts Tab */}
            <TabPanel px={0}>
              {withdrawnPosts.length === 0 ? (
                <VStack spacing={4} py={8}>
                  <Text fontSize="lg" color="gray.500">
                    No withdrawn posts
                  </Text>
                </VStack>
              ) : (
                                 <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={6}>
                   {withdrawnPosts.map((post) => (
                     <Box key={post.id} position="relative" opacity={0.7}>
                       <ItemCard
                         post={post}
                         onViewDetails={() => handleViewPost(post.id)}
                         onMessage={() => handleMessage(post.id)}
                       />
                      <Badge
                        position="absolute"
                        top={2}
                        left={2}
                        colorScheme="gray"
                        variant="solid"
                      >
                        WITHDRAWN
                      </Badge>
                    </Box>
                  ))}
                </Grid>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Message Modal */}
      <Modal isOpen={isMessageOpen} onClose={onMessageClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send Message</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
                          {currentPost && (
                <MessageInterface
                  postId={currentPost.id}
                  currentUserId={user.userId}
                  otherUserId="buyer-id"
                  onSendMessage={handleSendMessage}
                />
              )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
} 